import { styled } from '@linaria/react';
import { Fragment, memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BaseEdge,
  Controls,
  EdgeLabelRenderer,
  type EdgeProps,
  Handle,
  type NodeProps,
  Position,
  ReactFlow,
  ReactFlowProvider,
  getStraightPath,
} from '@xyflow/react';
import { Stages, WorkflowStatus } from '../../../api/process-client/process-client.type';
import { buildStatusLabelMap, createStatusLabelResolver } from '../../../helpers/status-labels/status-labels';
import { newCOLORS } from '../../../styles/colors';
import { secondaryBase } from '../../../styles/design-tokens';
import { getAnimatedDotRadius } from '../../process/helpers/edges.helpers';
import { getBackground, getBorder } from '../../process/nodes/stage-node.helpers';
import { ProcessGraphLegend } from '../../process/process-graph-legend';
import { Text as ProcessText } from '../../process/process.styled';
import { ViewType } from '../../process/process.type';
import { calculateGraph, getStageI18nKey } from '../../process/processv2.helpers';
import { WorkflowEdgeIn, WorkflowEdgeOut } from '../../process/edges/workflow-edge';
import type { ArtifactComponentProps } from './artifact-registry';
import {
  VIEW_TYPE_LABELS,
  getInitialShowEdges,
  getInitialViewType,
  isProcessMapPayload,
  type ProcessMapPayload,
} from './process-map-artifact.helpers';

/* ── Read-only StageNode ───────────────────────────────── */

const ReadOnlyStageNode = memo(({ data }: NodeProps) => {
  const { t } = useTranslation();
  const nodeData = data as {
    stage: string;
    title: string;
    customerStages?: string[];
    customerStageLabels?: Record<string, string>;
    isRightGraph?: boolean;
  };

  const isGrayed = nodeData.stage === Stages.Blocked || nodeData.stage === 'Waiting';
  const i18nKey = getStageI18nKey(nodeData.title);
  const translatedTitle: string = i18nKey != null ? t(i18nKey) : nodeData.title;

  return (
    <>
      <StageNodeContainer active grayed={isGrayed}>
        <ProcessText fontSize={12} active grayed={isGrayed} style={{ wordBreak: 'break-word' }}>
          {translatedTitle}
        </ProcessText>
        {nodeData.customerStages?.map((stage: string, index: number) => (
          <ProcessText
            key={stage}
            fontSize={14}
            fontWeight={700}
            active
            grayed={isGrayed}
            style={{ wordWrap: 'break-word' }}
          >
            {`${nodeData.customerStageLabels?.[stage] ?? stage}${
              index === (nodeData.customerStages?.length || 0) - 1 ? '' : ','
            }`}
          </ProcessText>
        ))}
      </StageNodeContainer>
      <Handle type="target" position={Position.Top} id="top" />
      <Handle type="source" position={Position.Bottom} id="bottom" />
      <Handle type="source" position={Position.Right} id="right" />
      <Handle type="target" position={Position.Right} id="right-target" />
      <Handle type="source" position={Position.Left} id="left" />
      <Handle type="target" position={Position.Left} id="left-target" />
    </>
  );
});

/* ── Read-only WorkflowNode ────────────────────────────── */

const ReadOnlyWorkflowNode = memo(({ data }: NodeProps) => {
  const nodeData = data as {
    value: number | string;
    status: WorkflowStatus;
    isRightGraph?: boolean;
  };
  const isBackwards = nodeData.status === WorkflowStatus.Backwards;

  return (
    <Fragment>
      <WorkflowContainer status={nodeData.status}>{nodeData.value}</WorkflowContainer>
      {isBackwards ? (
        <Fragment>
          <Handle type="source" position={Position.Top} id="top" />
          <Handle type="target" position={Position.Bottom} id="bottom" />
        </Fragment>
      ) : (
        <Fragment>
          <Handle type="target" position={Position.Top} id="top" />
          <Handle type="source" position={Position.Bottom} id="bottom" />
        </Fragment>
      )}
    </Fragment>
  );
});

/* ── Read-only StageEdge ───────────────────────────────── */

const ReadOnlyStageEdge = memo(({ data, sourceX, sourceY, targetX, targetY, style = {}, markerEnd }: EdgeProps) => {
  const edgeData = data as { value: number; sourceStage: string };
  const isBlocked = edgeData.sourceStage === Stages.Blocked;

  const [edgePath, labelX, labelY] = useMemo(
    () =>
      getStraightPath({
        sourceX,
        sourceY: sourceY - 5,
        targetX,
        targetY: targetY + 3,
      }),
    [sourceX, sourceY, targetX, targetY],
  );

  const showAnimatedDot = edgeData.value > 0 && edgePath;

  const relativePath = useMemo(() => {
    if (!edgePath) return '';
    const [endX, endY] = [targetX - sourceX, targetY + 3 - (sourceY - 5)];
    return `M0,0 L${endX},${endY}`;
  }, [sourceX, sourceY, targetX, targetY, edgePath]);

  // Use CSS variable for non-blocked edges so dark mode adapts automatically
  const edgeStroke = isBlocked ? style.stroke : 'var(--base-black, #000)';
  const edgeStyle = useMemo(() => ({ ...style, stroke: edgeStroke }), [style, edgeStroke]);

  const borderColor = isBlocked ? newCOLORS.purple2 : 'var(--base-black, #000)';
  const bgColor = isBlocked ? 'var(--base-white, #fff)' : 'var(--base-black, #000)';
  const textColor = isBlocked ? newCOLORS.purple2 : 'var(--base-white, #fff)';

  return (
    <>
      <BaseEdge path={edgePath} markerEnd={markerEnd} style={edgeStyle} />
      {showAnimatedDot && (
        <g transform={`translate(${sourceX},${sourceY - 5})`}>
          <circle r={getAnimatedDotRadius(style.strokeWidth)} fill={edgeStroke}>
            <animateMotion dur="2s" repeatCount="indefinite" path={relativePath} begin={`${Math.random() * 2}s`} />
          </circle>
        </g>
      )}
      <EdgeLabelRenderer>
        <div
          style={{
            position: 'absolute',
            transform: `translate(10%, -50%) translate(${labelX + 30}px,${labelY}px)`,
            pointerEvents: 'none',
          }}
          className="nodrag nopan"
        >
          <EdgeLabel borderColor={borderColor} bgColor={bgColor} textColor={textColor}>
            <EdgeLabelText>{edgeData.value}</EdgeLabelText>
          </EdgeLabel>
        </div>
      </EdgeLabelRenderer>
    </>
  );
});

/* ── Node/Edge type maps ───────────────────────────────── */

const readOnlyNodeTypes = {
  stageNode: ReadOnlyStageNode,
  workflowNode: ReadOnlyWorkflowNode,
};

const readOnlyEdgeTypes = {
  stageEdge: ReadOnlyStageEdge,
  workflowEdgeIn: WorkflowEdgeIn,
  workflowEdgeOut: WorkflowEdgeOut,
};

/* ── Main component ────────────────────────────────────── */

/**
 * Renders the Flora chat artifact view for backend-provided process-map data.
 *
 * @param props - Standard Flora artifact component props. `initialState` (when
 *   provided by the export route) seeds `viewType` and `showEdges`. See
 *   `ArtifactComponentProps` JSDoc for the recognized keys.
 * @returns Process-map graph (read-only ReactFlow) or empty state.
 */
export function ProcessMapArtifact({ data, initialState }: ArtifactComponentProps) {
  if (!isProcessMapPayload(data)) {
    return <EmptyState>Invalid process map data</EmptyState>;
  }

  return <ProcessMapGraph payload={data as unknown as ProcessMapPayload} initialState={initialState} />;
}

function ProcessMapGraph({
  payload,
  initialState,
}: {
  payload: ProcessMapPayload;
  initialState?: Record<string, unknown>;
}) {
  const i18n = useTranslation();
  const [viewType, setViewType] = useState<ViewType>(() => getInitialViewType(initialState));
  const [showEdges, setShowEdges] = useState(() => getInitialShowEdges(initialState));

  const graphData = payload.graph_data;
  const mapping = payload.mapping;
  const currencyCode = payload.currency_code || 'USD';
  const useGroups = payload.use_groups ?? true;

  // For projects (useGroups), stages come from the mapping which filters to BF sections.
  // For subprojects, stages come from raw graph data nodes.
  const allStages = useMemo(() => {
    if (!graphData?.nodes) return [];
    return [...graphData.nodes].sort((a, b) => a.order - b.order).map((n) => n.name);
  }, [graphData]);

  const activeStages = showEdges ? allStages : [];

  // Resolve customer stage IDs to their source-cased display labels using the
  // shared status-label helpers. When `statuses` is absent/empty the map is
  // empty and the resolver returns raw values, preserving the pre-existing
  // behavior (and keeping a backend-first rollout safe).
  //
  // `statuses` is untrusted backend data that `isProcessMapPayload` does not
  // validate, so guard against a non-array value here: `buildStatusLabelMap`
  // would call `.forEach` on it and throw inside this render-phase `useMemo`,
  // tearing down the tree.
  const resolveStatusLabel = useMemo(() => {
    const statuses = Array.isArray(payload.statuses) ? payload.statuses : undefined;
    return createStatusLabelResolver(buildStatusLabelMap(statuses));
  }, [payload.statuses]);

  const { nodes, edges } = useMemo(
    () =>
      calculateGraph(graphData, mapping, activeStages, viewType, useGroups, false, currencyCode, resolveStatusLabel),
    [graphData, mapping, activeStages, viewType, useGroups, currencyCode, resolveStatusLabel],
  );

  if (!graphData || !mapping || nodes.length === 0) {
    return <EmptyState>No process map data available</EmptyState>;
  }

  return (
    <GraphContainer>
      <HeaderRow>
        <ToggleRow>
          {Object.values(ViewType).map((vt) => (
            <ViewTypeBtn key={vt} $active={viewType === vt} onClick={() => setViewType(vt)}>
              {VIEW_TYPE_LABELS[vt]}
            </ViewTypeBtn>
          ))}
        </ToggleRow>

        <EdgeToggle $active={showEdges} onClick={() => setShowEdges((v) => !v)}>
          <EdgeToggleIcon $active={showEdges}>⇄</EdgeToggleIcon>
          Full View
        </EdgeToggle>
      </HeaderRow>

      <FlowContainer className="flora-process-map-graph">
        <ReactFlowProvider>
          <StyledReactFlow
            nodeOrigin={[0.5, 0.5]}
            fitView
            nodeTypes={readOnlyNodeTypes}
            edgeTypes={readOnlyEdgeTypes}
            nodes={nodes}
            edges={edges}
            proOptions={{ hideAttribution: true }}
            edgesFocusable={false}
            elementsSelectable={false}
            nodesDraggable={false}
            nodesConnectable={false}
          >
            <Controls position="bottom-right" showInteractive={false} />
            <div className="flora-legend-wrap">
              <ProcessGraphLegend
                legendItems={[
                  {
                    icon: 'square',
                    color: 'var(--base-black, #000)',
                    text: i18n.t('process.section_graph.legend.expected_steps'),
                    variant: 'filled',
                  },
                  {
                    icon: 'square',
                    color: newCOLORS.purple2,
                    text: i18n.t('process.section_graph.legend.skipped_steps'),
                    variant: 'filled',
                  },
                  {
                    icon: 'square',
                    color: newCOLORS.purple2,
                    text: i18n.t('process.section_graph.legend.backwards_steps'),
                    variant: 'outlined',
                  },
                  {
                    icon: 'square',
                    color: newCOLORS.gray3,
                    text: i18n.t('process.section_graph.legend.blocked_steps'),
                    variant: 'filled',
                  },
                ]}
                graphData={graphData}
                showOverview
              />
            </div>
          </StyledReactFlow>
        </ReactFlowProvider>
      </FlowContainer>
    </GraphContainer>
  );
}

/* ── Styled components ─────────────────────────────────── */

const EmptyState = styled.div`
  padding: 24px;
  text-align: center;
  color: var(--ink-lighter);
  font-size: 13px;
`;

const GraphContainer = styled.div`
  width: 100%;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  flex-wrap: wrap;
  gap: 8px;
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 2px;
  border: 1px solid var(--flora-table-border);
  border-radius: 8px;
  background: transparent;
  width: fit-content;
`;

const EdgeToggle = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border: 1px solid ${({ $active }) => ($active ? secondaryBase : 'var(--flora-table-border)')};
  border-radius: 8px;
  background: ${({ $active }) => ($active ? `${secondaryBase}14` : 'transparent')};
  color: ${({ $active }) => ($active ? secondaryBase : 'var(--ink-base)')};
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  transition:
    background 0.15s,
    color 0.15s,
    border-color 0.15s;

  &:hover {
    background: ${({ $active }) => ($active ? `${secondaryBase}20` : 'var(--flora-hover-strong)')};
  }
`;

const EdgeToggleIcon = styled.span<{ $active: boolean }>`
  font-size: 14px;
  opacity: ${({ $active }) => ($active ? 1 : 0.5)};
`;

const ViewTypeBtn = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 10px;
  border: none;
  border-radius: 6px;
  background: ${({ $active }) => ($active ? secondaryBase : 'transparent')};
  color: ${({ $active }) => ($active ? '#fff' : 'var(--ink-base)')};
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  transition:
    background 0.15s,
    color 0.15s;

  &:hover {
    background: ${({ $active }) => ($active ? secondaryBase : 'var(--flora-hover-strong)')};
  }
`;

const FlowContainer = styled.div`
  width: 100%;
  height: 500px;
`;

const StageNodeContainer = styled.div<{ active: boolean; grayed: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  width: 160px;
  border-radius: 6px;
  border: 1px solid ${(props: { active: boolean; grayed: boolean }) => getBorder(props.active, props.grayed)};
  background: ${(props: { active: boolean; grayed: boolean }) => getBackground(props.active, props.grayed)};
  padding: 12px 6px;
`;

const WorkflowContainer = styled.div<{ status: WorkflowStatus }>`
  min-width: 60px;
  max-width: 120px;
  height: 30px;
  padding: 0 8px;
  border-radius: 10px;
  border: 1px solid ${newCOLORS.purple2};
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ status }: { status: WorkflowStatus }) =>
    status === WorkflowStatus.Backwards ? newCOLORS.white : newCOLORS.purple2};
  color: ${({ status }: { status: WorkflowStatus }) =>
    status === WorkflowStatus.Backwards ? newCOLORS.purple2 : newCOLORS.white};
  text-align: center;
  font-family: Figtree;
  font-size: 12px;
  font-weight: 700;
  line-height: normal;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const EdgeLabel = styled.div<{ borderColor: string; bgColor: string; textColor: string }>`
  min-width: 60px;
  max-width: 120px;
  height: 30px;
  padding: 0 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 10px;
  border: 1px solid ${({ borderColor }) => borderColor};
  background: ${({ bgColor }) => bgColor};
  color: ${({ textColor }) => textColor};
  white-space: nowrap;
  overflow: hidden;
`;

const EdgeLabelText = styled.div`
  font-family: Figtree;
  font-size: 12px;
  font-weight: 700;
  line-height: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledReactFlow = styled(ReactFlow)`
  .react-flow__handle {
    opacity: 0;
    border: none;
    min-height: 0;
  }

  .react-flow__controls {
    background: #f5f5f5;
    box-shadow: var(--box-shadow-small-soft);
  }

  .react-flow__controls-button {
    background: transparent;
    border-bottom: 1px solid var(--flora-table-border);
    fill: var(--ink-dark);
  }

  .react-flow__controls-button:hover {
    background: var(--flora-hover-strong);
  }

  .flora-legend-wrap > div {
    max-width: 160px;
    padding: 8px 10px;
    font-size: 12px;
  }
`;
